import { useState, useEffect, useRef } from 'react'

import {PrimaryButton, ListingOnboardingForm, CustomCard, ProspectScreeningFrame, TourSchedulingFrame, ToursListFrame, ListingDescriptionFrame, Navigation } from '../../components';
import {
  Box,
  Container,
  Typography,
  Grid,
  Fab,
  ThemeProvider
} from '@mui/material'
import {
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  EventAvailableOutlined as EventAvailableOutlinedIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material'
import theme from '../../styles/theme'
import { trackFeatureUsage } from '../../services/FeatureTrackingService';

export default function MarketingPage() {
  const [showFloatingCTA, setShowFloatingCTA] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const firstCTARef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloatingCTA(!entry.isIntersecting && !showOnboarding)
      },
      {
        threshold: 0.1,
        rootMargin: '-50px 0px 0px 0px'
      }
    )

    if (firstCTARef.current) {
      observer.observe(firstCTARef.current)
    }

    return () => {
      if (firstCTARef.current) {
        observer.unobserve(firstCTARef.current)
      }
    }
  }, [showOnboarding])

  const handleStartCreating = () => {
    setShowOnboarding(true)
    setShowFloatingCTA(false) // Hide floating CTA when button is pressed
    // Smooth scroll to the form
      setTimeout(() => {
        document.getElementById('onboarding-form')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 100)

      trackFeatureUsage(null, 'Create in Seconds Button Clicked')
  }

  return (
    <ThemeProvider theme={theme}>
      <style>
        {`
          @keyframes ripple {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.7;
            }
          }
          @keyframes wave {
            0% {
              transform: translateY(0px);
            }
            25% {
              transform: translateY(-2px);
            }
            50% {
              transform: translateY(0px);
            }
            75% {
              transform: translateY(-1px);
            }
            100% {
              transform: translateY(0px);
            }
          }
        `}
      </style>

      <Navigation />

      {/* Hero Section */}
      <Box sx={{ bgcolor: 'white', paddingTop: { xs: 10, sm: 13, } }}>
        <Container maxWidth="lg" >
          <Box sx={{ maxWidth: 768, mx: 'auto', textAlign: 'left' }}>
            {/* Headline */}
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.875rem', sm: '2.25rem' },
                lineHeight: 1.2,
                color: 'grey.900',
                mb: 1.5
              }}
            >
              Serious tours on{' '}
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  position: 'relative',
                  paddingBottom: 1
                }}
              >
                autopilot
                <svg
                  width="100%"
                  height="8"
                  viewBox="0 0 100 8"
                  style={{
                    position: 'absolute',
                    bottom: '2px',
                    left: '0',
                    width: '100%',
                    height: '8px'
                  }}
                >
                  <path
                    d="M 0 2 Q 50 8 100 2"
                    stroke="#fbbf24"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
            </Typography>

            {/* Subtext */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', sm: '1.125rem' },
                color: 'grey.700',
                mb: 2
              }}
            >
              Set your criteria once. We screen renters and book tours around the clock — no back-and-forth.
            </Typography>

            {/* CTA */}
            <Box ref={firstCTARef}>
              <PrimaryButton
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon sx={{ color: 'white' }} />}
                onClick={handleStartCreating}
                sx={{
                  bgcolor: 'grey.900',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  mb: 2
                }}
              >
                Create in seconds
              </PrimaryButton>
              <Typography
                variant="body2"
                sx={{
                  color: 'grey.600',
                  textAlign: 'left',
                  marginLeft: '12px',
                  mb: 4
                }}
              >
                Free for landlords · No credit card required
              </Typography>
            </Box>

            {/* ToursListFrame */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Box sx={{
                maxWidth: 300,
                mx: 'auto',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <ToursListFrame />
              </Box>
            </Box>

          </Box>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ py: { xs: 5, md: 7 }, px: { xs: 2, md: 0 }, bgcolor: '#fef3c7' }}>
        <Container maxWidth="md" disableGutters={false}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: 'grey.900',
              textAlign: 'center',
              mb: 4
            }}
          >
            How it works
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 4 } }}>
            {[
              { number: '1', title: 'Set your criteria once', body: 'Tell us what you need in a renter. Takes seconds.' },
              { number: '2', title: 'Add your link to your listing', body: 'Drop it in your Facebook description. Renters can schedule a tour direclty with the link' },
              { number: '3', title: 'Serious renters show up', body: 'Pre-screened prospects pick from your available times and book. You wake up to a calendar.' }
            ].map((step) => (
              <Box key={step.number} sx={{ flex: 1, minWidth: 0 }}>
                <Feature
                  title={step.title}
                  icon={
                    <Box sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#fbbf24',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', color: 'white' }}>
                        {step.number}
                      </Typography>
                    </Box>
                  }
                >
                  {step.body}
                </Feature>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Prospect Screening Feature Section */}
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 1 } }}>
              <Box sx={{
                maxWidth: 300,
                mx: 'auto',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <ProspectScreeningFrame />
              </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 2 } }}>
              <Box sx={{ maxWidth: 500 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.875rem', sm: '2.25rem' },
                    color: 'grey.900',
                    mb: 2
                  }}
                >
                  Smart Prospect Screening
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'grey.600',
                    mb: 3,
                    fontWeight: 400,
                    fontSize: '1.125rem'
                  }}
                >
                  Pre-qualify prospects before they even see your property. Save time and focus on serious applicants.
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Feature
                    title="Focus on quality leads"
                    icon={<TrendingUpIcon sx={{ color: '#fbbf24' }} />}
                  >
                    Show your unit to qualified prospects only.
                  </Feature>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Tour Scheduling Feature Section */}
      <Box sx={{ py: { xs: 4, md: 5 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Box sx={{ maxWidth: 500 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.875rem', sm: '2.25rem' },
                    color: 'grey.900',
                    mb: 2
                  }}
                >
                  Effortless Tour Scheduling
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'grey.600',
                    mb: 3,
                    fontWeight: 400,
                    fontSize: '1.125rem'
                  }}
                >
                  Let prospects book tours on your schedule.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Feature
                    title="Create urgency"
                    icon={<AccessTimeIcon sx={{ color: '#fbbf24' }} />}
                  >
                    Show prospects how popular your listing is, while providing them a polished booking experience.
                  </Feature>
                </Box>
                <Box sx={{ mb: 4 }}>
                  <Feature
                    title="Set your availability once"
                    icon={<EventAvailableOutlinedIcon sx={{ color: '#fbbf24' }} />}
                  >
                    Add your availability and let our system handle the rest. Prospects see only your available times.
                  </Feature>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <Box sx={{
                maxWidth: 300,
                mx: 'auto',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <TourSchedulingFrame />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>


      {/* Listing Description Section */}
      <Box sx={{ py: { xs: 5, md: 6 }, paddingBottom: 3, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: { xs: 'left', md: 'center' }, mb: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.875rem', sm: '2.25rem' },
                color: 'grey.900',
                mb: 2
              }}
            >
              Hold up! (We make{' '}
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  position: 'relative',
                  paddingBottom: 1
                }}
              >
                dope
                <svg
                  width="100%"
                  height="8"
                  viewBox="0 0 100 8"
                  style={{
                    position: 'absolute',
                    bottom: '2px',
                    left: '0',
                    width: '100%',
                    height: '8px'
                  }}
                >
                  <path
                    d="M 0 2 Q 50 8 100 2"
                    stroke="#fbbf24"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
              {' '}descriptions too)
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'grey.600',
                mb: 3,
                fontWeight: 400,
                fontSize: '1.125rem',
                textAlign: { xs: 'left', md: 'center' }
              }}
            >
              Turn your location into your superpower 🦸.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box sx={{
              maxWidth: 300,
              mx: 'auto',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <ListingDescriptionFrame />
            </Box>
          </Box>
        </Container>
      </Box>

      <ListingOnboardingForm isVisible={showOnboarding} />

      {/* Floating CTA Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 1000,
          opacity: showFloatingCTA ? 1 : 0,
          transform: showFloatingCTA ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          pointerEvents: showFloatingCTA ? 'auto' : 'none'
        }}
      >
        <Fab
          variant="extended"
          color="primary"
          onClick={handleStartCreating}
          sx={{
            bgcolor: 'grey.900',
            color: 'white',
            px: 3,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              bgcolor: 'grey.800',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
            }
          }}
        >
          Create in seconds ✨
        </Fab>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'grey.200',
          py: 4,
          mt: 8
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'grey.600',
                fontSize: '0.875rem'
              }}
            >
              © {new Date().getFullYear()} HeyyProperty Inc. All rights reserved.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'grey.500',
                fontSize: '0.75rem'
              }}
            >
              Made with ❤️
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  )
}

// Feature component
interface FeatureProps {
  title: string
  children: React.ReactNode
  icon?: React.ReactNode
}

function Feature({ title, children, icon }: FeatureProps) {
  return (
    <CustomCard
      sx={{
        p: 1.75,
        height: '100%'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
        {icon && (
          <Box sx={{ color: 'primary.main', mt: 0.25 }}>
            {icon}
          </Box>
        )}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'grey.900',
            fontSize: '0.875rem'
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: 'grey.600',
          fontSize: '0.875rem',
          lineHeight: 1.5
        }}
      >
        {children}
      </Typography>
    </CustomCard>
  )
}
